import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsLeftRight, X } from "@phosphor-icons/react";

const ENTITY_LABELS = {
  vehicles: "Vehicles",
  customers: "Customers",
  sales: "Sales",
  users: "Staff users",
};

export default function BranchReassignDialog({ open, source, branches, onClose, onDone }) {
  const [target, setTarget] = useState("");
  const [entities, setEntities] = useState(["vehicles", "customers", "sales", "users"]);
  const [usage, setUsage] = useState(null);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open || !source) return;
    setTarget("");
    setEntities(["vehicles", "customers", "sales", "users"]);
    setUsage(null);
    api
      .get(`/branches/${source.id}/usage`)
      .then((res) => setUsage(res.data))
      .catch((err) => toast.error(formatApiError(err)));
  }, [open, source]);

  if (!open || !source) return null;

  const targetOptions = branches.filter((b) => b.id !== source.id && b.active);
  const toggleEntity = (key) =>
    setEntities((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const totalAffected = entities.reduce((acc, k) => acc + (usage?.[k] ?? 0), 0);
  const canSubmit = target && entities.length > 0 && totalAffected > 0;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setWorking(true);
    try {
      const { data } = await api.post(`/branches/${source.id}/reassign`, {
        to_branch_id: target,
        entities,
      });
      const moved = Object.entries(data.moved || {})
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${n} ${ENTITY_LABELS[k]?.toLowerCase()}`)
        .join(", ");
      toast.success(`Moved ${moved || "0 records"}`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="branch-reassign-dialog"
        onClick={(e) => e.stopPropagation()}
        className="bg-canvas-card border border-canvas-line w-full max-w-md shadow-2xl"
      >
        <header className="flex items-start justify-between p-5 border-b border-canvas-line">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
              Bulk reassign
            </div>
            <h2 className="text-lg font-semibold text-ink">{source.name}</h2>
          </div>
          <button
            data-testid="close-reassign"
            onClick={onClose}
            className="p-1 text-ink-muted hover:text-ink"
          >
            <X size={16} weight="bold" />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              Move records to
            </label>
            <select
              data-testid="reassign-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            >
              <option value="" disabled>
                Select target branch…
              </option>
              {targetOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            {targetOptions.length === 0 && (
              <div className="mt-2 text-[11px] text-ink-subtle">
                No other active branches to move to. Create one first.
              </div>
            )}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
              What to move
            </div>
            <div className="space-y-2">
              {Object.entries(ENTITY_LABELS).map(([key, label]) => {
                const count = usage?.[key] ?? 0;
                return (
                  <label
                    key={key}
                    className={`flex items-center justify-between p-3 border ${
                      entities.includes(key) ? "border-ink bg-canvas-panel" : "border-canvas-line"
                    } cursor-pointer transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        data-testid={`reassign-entity-${key}`}
                        checked={entities.includes(key)}
                        onChange={() => toggleEntity(key)}
                        disabled={count === 0}
                      />
                      <span className="text-sm text-ink">{label}</span>
                    </div>
                    <span
                      className={`font-mono text-xs ${count > 0 ? "text-ink" : "text-ink-subtle"}`}
                    >
                      {usage === null ? "…" : count}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {totalAffected > 0 && target && (
            <div className="border border-canvas-line bg-canvas-panel p-3 flex items-center gap-2 text-xs text-ink">
              <ArrowsLeftRight size={14} weight="bold" />
              <span>
                <strong className="font-semibold">{totalAffected}</strong> record(s) will be moved to{" "}
                <strong className="font-semibold">
                  {targetOptions.find((b) => b.id === target)?.name}
                </strong>
                .
              </span>
            </div>
          )}
        </div>

        <footer className="px-5 py-4 border-t border-canvas-line flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="confirm-reassign"
            onClick={handleSubmit}
            disabled={!canSubmit || working}
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-50"
          >
            {working ? "Moving…" : "Move records"}
          </button>
        </footer>
      </div>
    </div>
  );
}
